import { InputAdornment, TextField as MuiTextField } from '@mui/material';
import { cn } from 'cn';
import { type ComponentProps, forwardRef, type ReactNode, useId } from 'react';

import { isFieldRequired, isSafari, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type P = {
  readonly flex?: '1' | 'auto' | 'initial' | 'none';
  readonly hint?: string;
  readonly icon?: ReactNode;
  readonly unit?: string;
  readonly isDisabled?: boolean;
} & ComponentProps<typeof MuiTextField>;

export const TextField = forwardRef<HTMLInputElement, P>(
  (
    {
      'aria-label': ariaLabel,
      autoComplete = 'off',
      className,
      flex = '1',
      hint,
      isDisabled = false,
      label,
      placeholder,
      style,
      type = 'text',
      unit,
      ...rest
    },
    ref,
  ) => {
    const field = useFieldContext<string>();

    const isRequired = isFieldRequired(
      field.options.form.options.validators,
      field.name,
    );

    const hasErrors = Boolean(field.state.meta.errors?.length);

    const id = useId();

    // SAFARI BUG FIX: Native date/time inputs display phantom values
    // Safari shows misleading placeholder values (e.g., current date/time) in empty
    // date/time inputs, unlike Chrome/Firefox which show proper placeholders or blank fields.
    // This fix hides the phantom field values while preserving input structure.
    const isNativeDateOrTime = [
      'date',
      'time',
      'datetime-local',
      'month',
      'week',
    ].includes(type);
    const applySafariFix = isSafari && isNativeDateOrTime;
    const hasValue = Boolean(field.state.value);

    return (
      <div
        className={cn(
          'relative',
          {
            hidden: type === 'hidden',
          },
          className,
        )}
      >
        {Boolean(label) && type !== 'hidden' && (
          <InputLabel
            className={cn({
              'text-right': type === 'number',
            })}
            hint={hint}
            htmlFor={id}
            isRequired={isRequired}
          >
            {label}
          </InputLabel>
        )}
        <div
          className={cn(
            'flex items-center gap-x-2 overflow-hidden transition-colors duration-200 ease-out',
            {
              //   'border-tigaBorder-default focus-within:border-tigaAction-lighter hover:border-tigaAction-lighter':
              //     !(hasErrors || disabled),

              //   'border-tigaBorder-darker text-tigaText-lighter cursor-not-allowed':
              //     disabled,
              'flex-auto': flex === 'auto',
              'flex-initial': flex === 'initial',
              'flex-none': flex === 'none',
              hidden: type === 'hidden',
            },
          )}
          style={style}
        >
          <MuiTextField
            // SAFARI BUG FIX: Force DOM recreation when date/time inputs change between empty/filled
            // This ensures Safari properly updates the visual display when values are cleared
            key={
              applySafariFix
                ? field.state.value
                  ? 'filled'
                  : 'empty'
                : undefined
            }
            autoComplete={autoComplete}
            className={cn('w-full flex-1 focus:outline-none', {
              '[&_input]:text-right': type === 'number',
              // SAFARI BUG FIX: Hide phantom field values in empty date/time inputs
              // Targets individual WebKit pseudo-elements to hide misleading placeholder values
              // while preserving input structure (separators, borders, calendar/clock icons)
              '[&_input::-webkit-datetime-edit-day-field]:opacity-(--field-opacity,0)':
                applySafariFix,
              '[&_input::-webkit-datetime-edit-month-field]:opacity-(--field-opacity,0)':
                applySafariFix,
              '[&_input::-webkit-datetime-edit-year-field]:opacity-(--field-opacity,0)':
                applySafariFix,
              '[&_input::-webkit-datetime-edit-hour-field]:opacity-(--field-opacity,0)':
                applySafariFix,
              '[&_input::-webkit-datetime-edit-minute-field]:opacity-(--field-opacity,0)':
                applySafariFix,
              '[&_input::-webkit-datetime-edit-ampm-field]:opacity-(--field-opacity,0)':
                applySafariFix,
            })}
            style={{
              // SAFARI BUG FIX: Show field values only when input actually has data
              ...(applySafariFix && hasValue
                ? ({ '--field-opacity': '1' } as React.CSSProperties)
                : {}),
            }}
            disabled={isDisabled}
            error={hasErrors}
            id={id}
            inputRef={ref}
            placeholder={placeholder}
            size="small"
            slotProps={{
              // MUI spreads unrecognized root props onto the FormControl
              // wrapper, so an `aria-label` only names the control when it is
              // routed through the htmlInput slot.
              htmlInput: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
              input: {
                ...(unit
                  ? {
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          className="[&>p]:text-tiga-text-lighter"
                        >
                          {unit}
                        </InputAdornment>
                      ),
                    }
                  : undefined),
                className: 'bg-white',
              },
            }}
            type={type === 'number' ? 'text' : type}
            value={field.state.value ?? ''}
            onBlur={field.handleBlur}
            onChange={(event) => {
              field.handleChange(event.target.value);
            }}
            {...rest}
          />
        </div>
        {hasErrors && type !== 'hidden' ? (
          <ValidationError>
            {unique(field.state.meta.errors.map(({ message }) => message)).join(
              ', ',
            )}
          </ValidationError>
        ) : null}
      </div>
    );
  },
);

TextField.displayName = 'TextField';
